import { gql } from "lib/apolloClient";
import {
  projectFragments,
  projectGroupFragments,
  studentProjectPortfolioFragments,
} from "./InviteFragments";
import {
  studentFragments,
  supervisorFragments,
  projectGroupSupervisor,
  externalCollaboratorFragment,
  projectUserFragment,
} from "Projects/modules/ProjectFragments";
import { studentsAlertFragment } from "modules/CommonFragments";

export const getProjectGroupSupervisorsQuery = gql`
  query getProjectGroupSupervisors(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $projectFilters: ProjectGroupSupervisorProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              staff {
                ...userItem
                curriculumPrograms {
                  id
                  acronym
                }
              }
              projects(filters: $projectFilters) {
                id
                students {
                  ...userItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUserWithIsArchivedV2}
`;

export const getProjectGroupSupervisorsBasicDetailsQuery = gql`
  query getProjectGroupSupervisors(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          edges {
            node {
              id
              staff {
                ...supervisorInfo
              }
            }
          }
        }
      }
    }
  }
  ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
`;

export const getProjectGroupSupervisorsProjectsWithFieldsQuery = gql`
  query getProjectGroupSupervisorsProjectsWithFields(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $projectFieldFilters: PlannerFieldFilter
    $projectFilters: ProjectGroupSupervisorProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...projectGroupSupervisorApprovalInfoItem
              projects(filters: $projectFilters) {
                id
                fields(filters: $projectFieldFilters) {
                  id
                  uid
                  value
                }
                students {
                  ...studentInfo
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
  ${supervisorFragments.supervisorInfoFragment}
  ${projectGroupSupervisor.projectGroupSupervisorApprovalInfo}
`;

export const getProjectGroupSupervisorsWithPortfoliosQuery = gql`
  query getProjectGroupSupervisorsWithPortfolios(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $portfolioFilters: ProjectGroupSupervisorPortfoliosFilters
    $projectFilters: ProjectGroupSupervisorProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...projectGroupSupervisorApprovalInfoItem
              projects(filters: $projectFilters) {
                id
                fields(filters: { pseudoFieldUids: ["title", "image"] }) {
                  id
                  uid
                  value
                }
                type
                students {
                  ...studentInfo
                }
              }
              studentProjectPortfolios(filters: $portfolioFilters) {
                id
                student {
                  ...studentInfo
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
  ${projectGroupSupervisor.projectGroupSupervisorApprovalInfo}
`;

export const getProjectGroupExternalSupervisorsWithProjectsQuery = gql`
  query getProjectGroupExternalSupervisorsWithProjects(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $projectFilters: ProjectGroupSupervisorProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...projectGroupSupervisorApprovalInfoItem
              projects(filters: $projectFilters) {
                id
                fields(filters: { pseudoFieldUids: ["title", "image"] }) {
                  id
                  uid
                  value
                }
                type
                students {
                  ...studentInfo
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
  ${projectGroupSupervisor.projectGroupSupervisorApprovalInfo}
`;

export const getProjectGroupProjectsQuery = gql`
  query getProjectGroupProjects(
    $projectGroupId: ID!
    $filters: ProjectGroupProjectsFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projects(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...projectInfo
            }
          }
        }
      }
    }
  }
  ${projectFragments.projectInfoFragment}
`;

export const getProjectGroupStudentPortfoliosQuery = gql`
  query getProjectGroupStudentPortfolios(
    $projectGroupId: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ...portfolioStudentAdvisor
            }
          }
        }
      }
    }
  }
  ${studentProjectPortfolioFragments.studentProjectPortfolioDetailsFragment}
`;

export const getProjectGroupStudentsWithPortfoliosQuery = gql`
  query getProjectGroupStudentsWithPortfolios(
    $id: ID!
    $studentFilters: ProjectGroupStudentFilters
    $studentProjectPortfolioFilter: StudentProjectPortfolioFilter
    $academicYearId: ID
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        students(filters: $studentFilters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              student {
                ...projectGroupStudentsItem
              }
            }
          }
        }
      }
    }
  }
  ${projectGroupFragments.projectGroupStudentsFragmentV3}
`;

export const getProjectAllocationStatusQuery = gql`
  query getProjectAllocationStatus(
    $projectGroupId: ID!
    $filters: ProjectGroupProjectAllocationFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projectAllocationStatus(staffId: 0, filters: $filters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              allocated
              project {
                id
                students {
                  ...userItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUser}
`;

export const getProjectGroupProjectsCountQuery = gql`
  query getProjectGroupProjectsCount(
    $projectGroupId: ID!
    $filters: ProjectGroupProjectsFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projects(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupSupervisorsCountQuery = gql`
  query getProjectGroupSupervisorsCount(
    $projectGroupId: ID!
    $academicYearIds: [ID!]
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: { academicYearIds: $academicYearIds }) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupSupervisorsWithTypeCountQuery = gql`
  query getProjectGroupInternalSupervisorsCount(
    $projectGroupId: ID!
    $supervisorFilters: ProjectGroupSupervisorFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $supervisorFilters) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupStudentsQuery = gql`
  query getProjectGroupStudents(
    $id: ID!
    $filters: ProjectGroupStudentFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        students(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              student {
                ...projectGroupStudent
                projects(filters: { projectGroupId: $id }) {
                  id
                  title {
                    id
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${projectGroupFragments.projectGroupStudentFragment}
`;

export const getProjectGroupStudentsWithProjectsQuery = gql`
  query getProjectGroupStudentsWithProjects(
    $id: ID!
    $studentFilters: ProjectGroupStudentFilters
    $projectFieldFilters: PlannerFieldFilter
    $fetchFields: Boolean!
    $fetchProjects: Boolean!
    $fetchProjectStudents: Boolean!
    $academicYearId: ID
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        students(filters: $studentFilters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              student {
                ...projectGroupStudentsItem
              }
            }
          }
        }
      }
    }
  }
  ${projectGroupFragments.projectGroupStudentsFragmentV2}
`;

export const getProjectGroupStudentCountQuery = gql`
  query getProjectGroupStudentCount(
    $projectGroupId: ID!
    $filters: ProjectGroupStudentFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        students(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectStudentAllocationStatusQuery = gql`
  query getProjectStudentAllocationStatus(
    $projectGroupId: ID!
    $filters: ProjectGroupStudentAllocationFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projectStudentAllocationStatus(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              student {
                ...userItem
                alertTypes {
                  ...alertTypeFields
                }
              }
              allocated
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectUserFragment.basicUser}
`;

export const getProjectSupervisorAndStudentsQuery = gql`
  query getProjectSupervisorAndStudents($projectId: ID!) {
    node(id: $projectId, type: PROJECT) {
      ... on Project {
        id
        supervisors {
          ...userItem
        }
        students {
          ...userItem
        }
      }
    }
  }
  ${projectUserFragment.basicUser}
`;

export const getSupervisorProjectsQuery = gql`
  query getSupervisorProjects($staffId: ID!, $filters: ProjectFilter) {
    node(id: $staffId, type: STAFF) {
      ... on Staff {
        id
        projects(filters: $filters) {
          id
          students {
            ...userItem
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUser}
`;

export const getSupervisorPortfoliosQuery = gql`
  query getSupervisorPortfolios(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          edges {
            node {
              id
              student {
                ...studentInfo
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
`;

export const getProjectGroupPortfoliosStudentStaffQuery = gql`
  query getProjectGroupPortfoliosStudentStaff(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              student {
                ...studentInfo
              }
              staff {
                ...supervisorInfo
              }
            }
          }
        }
      }
    }
  }

  ${studentFragments.studentInfoFragment}
  ${supervisorFragments.supervisorInfoFragment}
`;

export const getProjectGroupStudentsPoolQuery = gql`
  query getProjectGroupStudents(
    $id: ID!
    $filters: ProjectGroupStudentFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        students(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              student {
                ...studentInfo
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
`;

export const getProjectGroupSupervisorsPoolQuery = gql`
  query getProjectGroupSupervisors(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              staff {
                ...supervisorInfo
              }
            }
          }
        }
      }
    }
  }
  ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
`;

export const getProjectGroupSupervisorApprovalInfoWithProjectsQuery = gql`
  query getProjectGroupSupervisorApprovalInfoWithProjects(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
    $projectFieldFilters: PlannerFieldFilter
    $projectFilters: ProjectGroupSupervisorProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          totalCount
          edges {
            node {
              ...projectGroupSupervisorApprovalInfoItem
              projects(filters: $projectFilters) {
                id
                fields(filters: $projectFieldFilters) {
                  id
                  uid
                  value
                }
                students {
                  ...studentInfo
                }
              }
            }
          }
        }
      }
    }
  }
  ${projectGroupSupervisor.projectGroupSupervisorApprovalInfo}
  ${studentFragments.studentInfoFragment}
`;

export const getProjectGroupInternalSupervisorPendingApprovalCountQuery = gql`
  query getProjectGroupInternalSupervisorPendingApprovalCount(
    $projectGroupId: ID!
    $academicYearIds: [ID!]
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(
          filters: {
            approvalStatus: [PENDING]
            supervisorType: INTERNAL
            academicYearIds: $academicYearIds
          }
        ) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupExternalSupervisorPendingApprovalCountQuery = gql`
  query getProjectGroupExternalSupervisorPendingApprovalCount(
    $projectGroupId: ID!
    $academicYearIds: [ID!]
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(
          filters: {
            approvalStatus: [PENDING]
            supervisorType: EXTERNAL
            academicYearIds: $academicYearIds
          }
        ) {
          totalCount
        }
      }
    }
  }
`;

export const getOrganizationStaffQuery = gql`
  query getOrganizationStaff(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
    $academicYearId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters) {
          totalCount
          edges {
            node {
              ...supervisorInfo
            }
          }
        }
      }
    }
  }
  ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
`;

export const getOrganizationExternalUsersQuery = gql`
  query getOrganizationStaff(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        externalUsers(filters: $filters) {
          totalCount
          edges {
            node {
              ...externalUserItem
            }
          }
        }
      }
    }
  }
  ${externalCollaboratorFragment.basicExternalCollaboratorV2}
`;

export const getProjectGroupExternalSupervisorsQuery = gql`
  query getProjectGroupExternalSupervisors(
    $projectGroupId: ID!
    $academicYearId: ID!
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(
          filters: {
            supervisorType: EXTERNAL
            orderBy: FIRST_NAME
            orderByDirection: ASC
            approvalStatus: [APPROVED]
            academicYearIds: [$academicYearId]
          }
        ) {
          totalCount
          edges {
            node {
              ...projectGroupSupervisorApprovalInfoItem
            }
          }
        }
      }
    }
  }
  ${projectGroupSupervisor.projectGroupSupervisorApprovalInfo}
`;
